import { useVirtualizer } from "@tanstack/react-virtual";
import { ScrollArea } from "@timeghost/ui/shadcn/scroll-area";
import { type JSX, type ReactNode, useEffect, useRef } from "react";
import { useInfiniteScrollSentinel } from "@/lib/hooks/use-infinite-scroll-sentinel";
import { cn } from "@/lib/utils";

const noopNearEnd = (): void => {};

export interface VirtualScrollListProps {
	readonly children: (index: number) => ReactNode;
	readonly className?: string;
	readonly count: number;
	readonly estimateSize?: number | ((index: number) => number);
	readonly gap?: number;
	readonly getItemKey?: (index: number) => string | number;
	readonly nearEndEnabled?: boolean;
	readonly onNearEnd?: () => void;
	readonly overscan?: number;
	readonly remasureKey?: number | string;
	readonly scrollAlign?: "auto" | "center" | "end" | "start";
	readonly scrollToIndex?: number;
	readonly viewportClassName?: string;
}

/**
 * ScrollArea + tanstack virtual for flat listings.
 * Prefer this over native overflow for any growing list.
 */
export default function VirtualScrollList({
	count,
	estimateSize = 52,
	gap = 0,
	getItemKey,
	nearEndEnabled = true,
	onNearEnd,
	overscan = 8,
	className,
	viewportClassName,
	remasureKey,
	scrollAlign = "center",
	scrollToIndex,
	children,
}: VirtualScrollListProps): JSX.Element {
	const viewportRef = useRef<HTMLDivElement>(null);
	const virtualizer = useVirtualizer({
		count,
		getScrollElement: () => viewportRef.current,
		estimateSize: (index) => (typeof estimateSize === "function" ? estimateSize(index) : estimateSize),
		gap,
		getItemKey,
		overscan,
	});
	const totalSize = virtualizer.getTotalSize();

	useInfiniteScrollSentinel({
		enabled: Boolean(onNearEnd) && nearEndEnabled,
		onIntersect: onNearEnd ?? noopNearEnd,
		rootRef: viewportRef,
	});

	useEffect(() => {
		if (scrollToIndex == null && remasureKey === undefined) {
			return;
		}
		let cancelled = false;
		const frames: number[] = [];
		const run = (): boolean => {
			const viewport = viewportRef.current;
			if (!viewport || viewport.clientHeight <= 0) {
				return false;
			}
			virtualizer.measure();
			if (scrollToIndex != null && scrollToIndex >= 0 && scrollToIndex < count) {
				virtualizer.scrollToIndex(scrollToIndex, { align: scrollAlign });
			}
			return true;
		};
		const raf = (fn: () => void): void => {
			frames.push(window.requestAnimationFrame(fn));
		};
		if (run()) {
			raf(() => {
				if (!cancelled) {
					run();
				}
			});
		} else {
			raf(() => {
				if (cancelled) {
					return;
				}
				run();
				raf(() => {
					if (!cancelled) {
						run();
					}
				});
			});
		}
		return () => {
			cancelled = true;
			for (const frame of frames) {
				window.cancelAnimationFrame(frame);
			}
		};
	}, [count, remasureKey, scrollAlign, scrollToIndex, virtualizer]);

	return (
		<ScrollArea
			className={cn("min-h-0 min-w-0", className)}
			viewportClassName={cn("overflow-anchor-none", viewportClassName)}
			viewportRef={viewportRef}>
			<div className="relative w-full" style={{ height: totalSize }}>
				{virtualizer.getVirtualItems().map((virtualRow) => (
					<div
						className="absolute top-0 left-0 w-full"
						data-index={virtualRow.index}
						key={virtualRow.key}
						ref={virtualizer.measureElement}
						style={{ transform: `translateY(${virtualRow.start}px)` }}>
						{children(virtualRow.index)}
					</div>
				))}
			</div>
		</ScrollArea>
	);
}
